import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import "./Journey_Section.css";

const milestones = [
    {
        year: "2021",
        title: "Foundations",
        description: "Started with C++ through Apna College. Struggled but persisted through early challenges, taking a full week just to run my first 'Hello World'.",
        icon: "💻",
        color: "#ff6b6b",
        achievements: ["First C++ program", "Basic programming concepts", "Problem-solving fundamentals"],
        challenges: ["Debugging environment issues", "Understanding memory management", "Syntax errors"],
        tools: ["C++", "Visual Studio", "Stack Overflow"]
    },
    {
        year: "2022",
        title: "Web & Languages",
        description: "Mastered HTML, CSS & JavaScript. Simultaneously learned Python. Began building my first web projects and practicing DSA fundamentals.",
        icon: "🌐",
        color: "#4ecdc4",
        achievements: ["First responsive website", "JavaScript DOM manipulation", "Python automation scripts"],
        challenges: ["CSS layouts", "JavaScript asynchronous concepts", "Balancing multiple languages"],
        tools: ["HTML/CSS", "JavaScript", "Python", "VSCode", "Git"]
    },
    {
        year: "2023",
        title: "Frontend to Backend",
        description: "Conquered React.js along with other frontend frameworks. Expanded into backend development with Flask and MongoDB. Built multiple full-stack projects.",
        icon: "⚙️",
        color: "#ffd166",
        achievements: ["Full-stack applications", "Database design", "API development"],
        challenges: ["State management", "Authentication flows", "Database optimization"],
        tools: ["React.js", "Flask", "MongoDB", "REST APIs", "Postman"]
    },
    {
        year: "2024",
        title: "AI & Security",
        description: "Diving into Machine Learning and Deep Learning while exploring Cybersecurity fundamentals. Continuing DSA practice for enjoyment and skill sharpening.",
        icon: "🧠",
        color: "#118ab2",
        achievements: ["Trained ML models", "Data analysis projects", "Network security basics"],
        challenges: ["Model optimization", "Working with large datasets", "Security vulnerabilities"],
        tools: ["TensorFlow", "Scikit-learn", "Jupyter Notebooks", "Wireshark"]
    },
    {
        year: "2025",
        title: "Deep Expertise",
        description: "Continuing my journey in Deep Learning with advanced models and applications. Expanding knowledge in Cybersecurity with practical defense techniques.",
        icon: "🔐",
        color: "#9b5de5",
        achievements: ["Advanced AI model development", "Security auditing skills", "Research contributions"],
        challenges: ["Explainable AI", "Zero-day vulnerabilities", "Ethical considerations"],
        tools: ["PyTorch", "Transformers", "Kali Linux", "Cloud Security platforms"]
    },
];

export function JourneySection() {
    const [expandedIndex, setExpandedIndex] = useState(null);

    return (
        <section className="journey-section">
            <div className="stars-container">
                <div className="stars"></div>
                <div className="stars2"></div>
                <div className="stars3"></div>
            </div>

            <div className="journey-container">
                <motion.div
                    className="journey-header"
                    initial={{ opacity: 0, y: -30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.8 }}
                >
                    <h2>My Coding Evolution</h2>
                    <p>A journey through time, challenges and technology</p>
                    <motion.div
                        className="header-underline"
                        initial={{ width: 0 }}
                        animate={{ width: "120px" }}
                        transition={{ duration: 0.8, delay: 0.4 }}
                    />
                </motion.div>

                <div className="timeline">
                    {milestones.map((milestone, index) => (
                        <motion.div
                            key={index}
                            className={`milestone ${expandedIndex === index ? 'expanded' : ''}`}
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.7, delay: index * 0.15 }}
                            viewport={{ once: true, margin: "-50px" }}
                        >
                            <div className="milestone-connector">
                                <div
                                    className="connector-line"
                                    style={{
                                        background: index < milestones.length - 1
                                            ? `linear-gradient(to bottom, ${milestone.color}, ${milestones[index + 1].color})`
                                            : `linear-gradient(to bottom, ${milestone.color}, transparent)`
                                    }}
                                />
                                <motion.div
                                    className="milestone-icon"
                                    style={{
                                        borderColor: milestone.color,
                                        boxShadow: `0 0 15px ${milestone.color}80`
                                    }}
                                    whileHover={{ scale: 1.2, rotate: 10 }}
                                    transition={{ type: "spring", stiffness: 400, damping: 10 }}
                                >
                                    {milestone.icon}
                                </motion.div>
                            </div>

                            <motion.div
                                className="milestone-card"
                                style={{
                                    borderColor: `${milestone.color}40`,
                                    boxShadow: expandedIndex === index ? `0 15px 30px rgba(0,0,0,0.3), 0 0 0 1px ${milestone.color}30` : ''
                                }}
                                whileHover={{ y: -5 }}
                                onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                            >
                                <div
                                    className="milestone-year"
                                    style={{ background: `linear-gradient(135deg, ${milestone.color}, ${milestone.color}cc)` }}
                                >
                                    {milestone.year}
                                </div>
                                <h3 style={{ color: milestone.color }}>{milestone.title}</h3>
                                <p>{milestone.description}</p>

                                {index === milestones.length - 1 && (
                                    <div className="badge current">Current Focus</div>
                                )}

                                <div className="card-footer">
                                    <button
                                        className="details-toggle"
                                        style={{ color: milestone.color }}
                                    >
                                        {expandedIndex === index ? 'Show Less' : 'Show More'}
                                    </button>
                                </div>

                                <AnimatePresence>
                                    {expandedIndex === index && (
                                        <motion.div
                                            className="milestone-details"
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: 'auto' }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                        >
                                            <div className="details-section">
                                                <h4>Key Achievements</h4>
                                                <ul>
                                                    {milestone.achievements.map((item, i) => (
                                                        <motion.li
                                                            key={i}
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: i * 0.1 }}
                                                        >
                                                            {item}
                                                        </motion.li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="details-section">
                                                <h4>Challenges Overcome</h4>
                                                <ul>
                                                    {milestone.challenges.map((item, i) => (
                                                        <motion.li
                                                            key={i}
                                                            initial={{ opacity: 0, x: -10 }}
                                                            animate={{ opacity: 1, x: 0 }}
                                                            transition={{ delay: 0.3 + i * 0.1 }}
                                                        >
                                                            {item}
                                                        </motion.li>
                                                    ))}
                                                </ul>
                                            </div>

                                            <div className="tech-tools">
                                                {milestone.tools.map((tool, i) => (
                                                    <motion.span
                                                        key={i}
                                                        className="tool-chip"
                                                        style={{ backgroundColor: `${milestone.color}20`, color: milestone.color }}
                                                        initial={{ opacity: 0, scale: 0.8 }}
                                                        animate={{ opacity: 1, scale: 1 }}
                                                        transition={{ delay: 0.6 + i * 0.05 }}
                                                    >
                                                        {tool}
                                                    </motion.span>
                                                ))}
                                            </div>
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </motion.div>
                        </motion.div>
                    ))}
                </div>
            </div>
        </section>
    );
}

export default JourneySection;